import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import {
  useGetSuppliesQuery,
  useDeleteSupplyMutation,
} from "../../slices/suppliesApiSlice";
import { toast } from "react-toastify";
import { useState } from "react";

const SupplyListScreen = () => {
  const [filter, setFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data, isLoading, error, refetch } = useGetSuppliesQuery(pageNumber);
  const [deleteSupply, { isLoading: loadingDelete }] = useDeleteSupplyMutation();

  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this supply?")) {
      try {
        await deleteSupply(id).unwrap();
        toast.success("Supply deleted successfully!");
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || "Failed to delete supply.");
      }
    }
  };

  const filterSupplies = () => {
    if (!data?.supplies) return [];
    return data.supplies.filter((supply) => {
      const date = new Date(supply.date);
      const isInRange =
        (!startDate || date >= new Date(startDate)) &&
        (!endDate || date <= new Date(endDate));

      const matchesGeneric =
        supply.note?.toLowerCase().includes(filter.toLowerCase()) ||
        supply.supplierDetails?.name?.toLowerCase().includes(filter.toLowerCase()) ||
        supply.supplyItems.some((item) =>
          item.name.toLowerCase().includes(filter.toLowerCase())
        );

      const matchesProduct =
        !productFilter ||
        supply.supplyItems.some((item) =>
          item.name.toLowerCase().includes(productFilter.toLowerCase())
        );

      return isInRange && matchesGeneric && matchesProduct;
    });
  };
   
  // Calculate total supplies revenue
  const calculateTotalRevenue = () => {
    return filterSupplies().reduce((totalRevenue, supply) => {
      const supplyRevenue = supply.supplyItems.reduce(
        (subTotalRevenue, item) => subTotalRevenue + item.qty * item.price,
        0
      );
      return totalRevenue + supplyRevenue;
    }, 0);
  };

   // Calculate total supplies quantity
  const calculateTotalQty = () => {
    return filterSupplies().reduce((totalQty, supply) => {
      const supplyQty = supply.supplyItems.reduce(
        (subTotalQty, item) => subTotalQty + item.qty ,
        0
      );
      return totalQty + supplyQty;
    }, 0);
  };
  // Calculate total supplies cost
  const calculateTotalCost = () => {
    return filterSupplies().reduce((totalCost, supply) => {
      const supplyCost = supply.supplyItems.reduce(
        (subTotalCost, item) => subTotalCost + item.itemCost,
        0
      );
      return totalCost + supplyCost;
    }, 0);
  };

  // Calculate total supplies profit
  const calculateTotalProfit = () => {
    return filterSupplies().reduce((totalProfit, supply) => {
      const supplyProfit = supply.supplyItems.reduce(
        (subTotalProfit, item) => subTotalProfit + item.profit,
        0
      );
      return totalProfit + supplyProfit;
    }, 0);
  };

  
const exportSuppliesToExcel = (supplies, filename = "Supplies.xlsx") => {
  const rows = [];

  supplies.forEach((supply) => {
    supply.supplyItems.forEach((item) => {
      rows.push({
        Date: new Date(supply.date).toLocaleDateString(),
        Product: item.name,
        Quantity: item.qty,
        Revenue: item.qty * item.price,
        Cost: item.itemCost,
        Profit: item.profit,
        Note: supply.note || "",
        Supplier: supply.supplierDetails?.name || "",
      });
    });
  });

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Supplies");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, filename);
};

const downloadAllSupplyImages = async () => {
  const zip = new JSZip();
  const folder = zip.folder("All_Supply_Images");

  const imagePromises = filterSupplies().flatMap((supply) =>
    supply.supplyItems.flatMap((item) =>
      item.images?.map(async (imagePath) => {
        try {
          const response = await fetch(imagePath.replace(/\\/g, "/"));
          const blob = await response.blob();
          const urlParts = imagePath.split("/");
          const filename = urlParts[urlParts.length - 1];

          folder.file(filename, blob);
        } catch (error) {
          console.error(`Failed to fetch ${imagePath}`, error);
        }
      }) || []
    )
  );

  await Promise.all(imagePromises);
  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, "All_Supply_Images.zip");
  });
};
  return (
    <>
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Supplies</h1>
          <Button variant="success" onClick={() => exportSuppliesToExcel(filterSupplies())}>
          Export to Excel
          </Button>
          <Button variant="info" className="mx-2" onClick={downloadAllSupplyImages}>
          Download All Images
          </Button>
        </Col>
      </Row>

      {/* 🔍 Filter Section */}
      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by note or supplier"
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="text"
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            placeholder="Filter by product name"
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12} className="text-end">
          <Button onClick={refetch}>Reset</Button>
        </Col>
      </Row>

      {/* Totals Display */}
      <Row className="mb-3">
        <Col md={3}>
          <h4>Total Revenue: ${calculateTotalRevenue().toFixed(2)}</h4>
        </Col>
        <Col md={3}>
          <h4>Total Qty: ${calculateTotalQty().toFixed(2)}</h4>
        </Col>
        <Col md={3}>
          <h4>Total Cost: ${calculateTotalCost().toFixed(2)}</h4>
        </Col>
        
        <Col md={3} className="text-end">
          <h4>Total Profit: ${calculateTotalProfit().toFixed(2)}</h4>
        </Col>
      </Row>

     
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || "An error occurred"}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>REV</th>
                <th>COST</th>
                <th>PROFIT</th>
                <th>NOTE</th>
                <th>SUPPLIER</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterSupplies().map((supply) =>
                supply.supplyItems.map((item) => (
                  <tr key={`${supply._id}-${item.product}`}>
                    <td>{new Date(supply.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>${item.itemCost}</td>
                    <td>${item.profit.toFixed(2)}</td>
                    
                    <td>{supply.note || "N/A"}</td>
                    <td>{supply.supplierDetails.name || "N/A"}</td>
                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt="Product"
                          style={{
                            width: "10px",
                            height: "10px",
                            objectFit: "cover",
                            borderRadius: "5px",
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button as={Link} to={`/supply/${supply._id}`} variant="light" className="btn-sm">Details</Button>
                      <Button as={Link} to={`/supply/${supply._id}/edit`} variant="light" className="btn-sm mx-2">Edit</Button>
                      <Button variant="danger" className="btn-sm" onClick={() => deleteHandler(supply._id)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => setPageNumber(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default SupplyListScreen;